/**
 * useIpdriftRecheck — the droom's SILENT responder to an officer IP-drift re-check
 * (testmap #13; the be's landed wire — see api/ipdrift.ts).
 *
 * PASSIVE class (SPEC-06 §3.3): zero customer-visible interaction. When the officer requests a
 * re-check, the be parks a PENDING request; this hook's in-call poll sees it and answers with one
 * empty POST — the be resolves the device's CURRENT source IP at the edge from that very request
 * and runs the fresh comparison against the session baseline. No UI, no permission prompt, no
 * interruption: the customer's device answers the way it already answers every heartbeat, just on
 * a wire the officer's request is bound to.
 *
 * Discipline encoded here:
 *   • ONE answer per request id (a ref-latch): the poll re-serving a request mid-completion must
 *     not double-post. The be's request FSM refuses duplicates anyway (404) — the latch just keeps
 *     this client from relying on that.
 *   • Poll and capture failures are SILENT no-ops — the call never degrades over this side
 *     channel; an unanswered request lazily expires server-side as an honest recorded outcome.
 *   • Runs only while the caller says the transport is genuinely up (`enabled`) — same cadence
 *     idiom as useCapturePrompt (5s: a live officer is waiting on the answer).
 */

import { useEffect, useRef } from 'react';
import { captureIpdriftRecheck, fetchPendingIpdriftRecheck } from '@/api/ipdrift';

const RECHECK_POLL_MS = 5_000;

export function useIpdriftRecheck(
  sessionId: string,
  enabled: boolean,
  opts: { intervalMs?: number } = {},
): void {
  const intervalMs = opts.intervalMs ?? RECHECK_POLL_MS;
  // Request ids this client already answered (or found terminal) — one answer per request.
  const answeredIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    let active = true;
    let timer: number | undefined;

    const tick = () => {
      fetchPendingIpdriftRecheck(sessionId)
        .then(async (req) => {
          if (!active || !req) return;
          if (req.state !== 'PENDING' || answeredIds.current.has(req.id)) return;
          answeredIds.current.add(req.id);
          try {
            await captureIpdriftRecheck(sessionId);
          } catch {
            // 404 = the request went terminal meanwhile (answered elsewhere / expired) — done.
            // Anything else: leave the latch set; the officer re-requests if they still care,
            // and the passive observer (#5) keeps watching every authed request regardless.
          }
        })
        .catch(() => {
          // Silent: the call never degrades over a side-channel read; next tick retries.
        })
        .finally(() => {
          if (active) timer = window.setTimeout(tick, intervalMs);
        });
    };

    tick();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [sessionId, enabled, intervalMs]);
}
